import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/utils-buddy";
import { Eye, EyeOff, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgot, setShowForgot] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Support ?mode=signup (used by the Buddy browser extension Sign Up button)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("mode") === "signup") setIsLogin(false);
    if (params.get("auth_error")) {
      setError("Google sign-in didn't complete. Please try again, or sign in with email and password below.");
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Uses Emergent Auth's built-in Google Sign-In. Browser lands on /auth/callback#session_id=xxx
    // after Google consent; AuthCallback.js exchanges the session_id for our JWT.
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await resp.json();
      setSuccess(data.message || "If an account exists, a reset link has been sent.");
    } catch {
      setError("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (showForgot) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--surface-cream)" }}>
        <div className="w-full max-w-[420px]">
          <div className="text-center mb-8 animate-fadeIn">
            <button onClick={() => navigate("/")} className="inline-block mb-6">
              <h1 className="font-display text-3xl font-bold" style={{ color: "var(--rolex-green)" }}>Buddy</h1>
            </button>
            <h2 className="font-display text-2xl font-medium" style={{ color: "var(--text-primary)" }}>Reset Password</h2>
            <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>Enter your email and we'll send you a reset link</p>
          </div>
          <div className="buddy-card p-8 animate-slideUp">
            {error && <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: "var(--error-bg)", color: "var(--error)" }}>{error}</div>}
            {success && <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: "var(--success-bg)", color: "var(--success)" }}>{success}</div>}
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="label-caps block mb-1.5">Email</label>
                <input type="email" className="buddy-input" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
              </div>
              <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2 mt-6" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : "Send Reset Link"}
              </button>
            </form>
          </div>
          <p className="text-center mt-6 text-sm" style={{ color: "var(--text-muted)" }}>
            <button onClick={() => { setShowForgot(false); setError(""); setSuccess(""); }} className="font-medium hover:underline" style={{ color: "var(--rolex-green)" }}>Back to Sign In</button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--surface-cream)" }}>
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8 animate-fadeIn">
          <button onClick={() => navigate("/")} className="inline-block mb-6" data-testid="auth-logo-link">
            <h1 className="font-display text-3xl font-bold" style={{ color: "var(--rolex-green)" }}>Buddy</h1>
          </button>
          <h2 className="font-display text-2xl font-medium" style={{ color: "var(--text-primary)" }}>
            {isLogin ? "Welcome back" : "Create your account"}
          </h2>
          <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
            {isLogin ? "Sign in to continue crafting perfect messages" : "Start writing like the best version of yourself"}
          </p>
        </div>

        <div className="buddy-card p-8 animate-slideUp" style={{ animationDelay: "0.1s" }}>
          {error && <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: "var(--error-bg)", color: "var(--error)" }} data-testid="auth-error">{error}</div>}

          {/* Google Login */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-lg border mb-5 text-sm font-medium transition-colors hover:bg-[var(--surface-warm)]"
            style={{ borderColor: "var(--surface-border)", color: "var(--text-secondary)" }}
            data-testid="google-login-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px" style={{ background: "var(--surface-border)" }} />
            <span className="text-xs" style={{ color: "var(--text-light)" }}>or</span>
            <div className="flex-1 h-px" style={{ background: "var(--surface-border)" }} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="label-caps block mb-1.5">Full Name</label>
                <input type="text" className="buddy-input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name-input" />
              </div>
            )}
            <div>
              <label className="label-caps block mb-1.5">Email</label>
              <input type="email" className="buddy-input" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="auth-email-input" />
            </div>
            <div>
              <label className="label-caps block mb-1.5">Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} className="buddy-input pr-10" placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="auth-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} data-testid="auth-toggle-password">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {isLogin && (
                <button type="button" onClick={() => { setShowForgot(true); setError(""); }} className="text-xs mt-1.5 hover:underline" style={{ color: "var(--rolex-green)" }} data-testid="forgot-password-link">
                  Forgot password?
                </button>
              )}
            </div>
            <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2 mt-6" disabled={loading} data-testid="auth-submit-btn">
              {loading ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <>{isLogin ? "Sign In" : "Create Account"}<ArrowRight size={16} /></>}
            </button>
          </form>
        </div>

        <p className="text-center mt-6 text-sm" style={{ color: "var(--text-muted)" }}>
          {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
          <button onClick={() => { setIsLogin(!isLogin); setError(""); }} className="font-medium hover:underline" style={{ color: "var(--rolex-green)" }} data-testid="auth-toggle-mode">
            {isLogin ? "Sign up free" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
}
